import { useEffect, RefObject } from 'react';

interface KeyboardFocusProps {
  /**
   * The ref of the element to apply the keyboard focus class to.
   */
  ref: RefObject<HTMLElement>;
  /**
   * Class to apply to the element when it has keyboard focus. Default is 'keyboard-focus'
   */
  className?: string;
}

enum FocusMethod {
  Mouse = 'mouse',
  Keyboard = 'keyboard',
}

export const KEYBOARD_FOCUS_CLASS = 'keyboard-focus';

/**
 * This hook applies a class to an element when it has keyboard focus.
 * @deprecated This class should be avoided. If you are trying to apply custom styles
 * to elements on focus, please search for built-in accesibilty APIs that are generally provided
 * by style/component libraries.
 *
 * E.g for radix components - https://www.radix-ui.com/primitives/docs/overview/accessibility
 * E.g for mui - https://stackoverflow.com/a/67139934
 *
 * Capturing and handling focus of HTML elements is best left to the browser.
 *
 * @param ref The ref of the element to apply the keyboard focus class to.
 * @param className Class to apply to the element when it has keyboard focus.
 */
const useKeyboardFocus = ({
  ref,
  className = KEYBOARD_FOCUS_CLASS,
}: KeyboardFocusProps) => {
  const isContainedOrEqual = (
    elA: HTMLElement | null,
    elB: HTMLElement | null
  ) => elA && elB && (elA === elB || elA.contains(elB));

  useEffect(() => {
    if (!ref.current) return;

    let lastFocusMethod = FocusMethod.Mouse;

    const handleMouseDown = () => {
      lastFocusMethod = FocusMethod.Mouse;
    };

    const handleKeyDown = () => {
      lastFocusMethod = FocusMethod.Keyboard;
    };

    const handleFocusIn = (event: FocusEvent) => {
      if (
        isContainedOrEqual(ref.current, event.target as HTMLElement) &&
        lastFocusMethod === FocusMethod.Keyboard
      ) {
        ref?.current?.classList.add(className);
      }
    };

    const handleFocusOut = (event: FocusEvent) => {
      if (isContainedOrEqual(ref.current, event.target as HTMLElement)) {
        ref?.current?.classList.remove(className);
      }
    };

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
    };
  }, [ref, className]);
};

export default useKeyboardFocus;
